/**
 * Free Shipping Progress Bar
 * Handles dynamic updates to the free shipping progress bar when cart changes
 */

class FreeShippingProgress {
  constructor() {
    this.progressContainer = null;
    this.progressElement = null;
    this.progressText = null;
    this.currentAmount = null;
    this.targetAmount = null;
    this.threshold = 0;
    this.lastKnownCartTotal = 0; // Track the last known cart total to prevent reverting to 0
    this.init();
  }

  init() {
    // Listen for cart updates
    document.addEventListener('cart:updated', (event) => {
      if (event.detail && event.detail.cart) {
        this.findProgressElements();
        // Store the cart total from the event
        const cartTotal = event.detail.cart.total_price || 0;
        this.lastKnownCartTotal = cartTotal;
        this.updateProgress(event.detail.cart);
      }
    });

    // Listen for cart drawer rendered events
    document.addEventListener('cart-drawer:rendered', (event) => {
      this.findProgressElements();
      if (event.detail && event.detail.cartData && event.detail.cartData.total_price !== undefined) {
        // Use cart data from the event if available
        const cartTotal = event.detail.cartData.total_price || 0;
        this.lastKnownCartTotal = cartTotal;
        this.updateProgress(event.detail.cartData);
      } else {
        // Only fall back to DOM reading if we don't have recent cart data
        setTimeout(() => {
          this.updateProgressFromCart();
        }, 150); // Slightly longer delay to ensure DOM is fully updated
      }
    });

    // Listen for cart drawer opened events
    document.addEventListener('cart-drawer:opened', () => {
      this.findProgressElements();
      // Use last known cart total if available, otherwise read from DOM
      if (this.lastKnownCartTotal > 0) {
        this.updateProgress({ total_price: this.lastKnownCartTotal });
      } else {
        this.updateProgressFromCart();
      }
    });

    // Initial setup: wait for correct currency and DOM
    this.waitForShopifyCurrency(() => {
      this.findProgressElements();
      this.updateProgressFromCart();
    });
  }

  waitForShopifyCurrency(callback, retries = 10) {
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      callback();
    } else if (retries > 0) {
      setTimeout(() => {
        this.waitForShopifyCurrency(callback, retries - 1);
      }, 100);
    } else {
      callback(); // fallback, run anyway
    }
  }

  findProgressElements() {
    this.progressContainer = document.querySelector('.free-shipping-progress');
    if (!this.progressContainer) return;

    // Hide if cart is empty
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('is-empty')) {
      this.progressContainer.style.display = 'none';
      return;
    } else if (this.progressContainer) {
      this.progressContainer.style.display = 'block';
    }

    this.progressElement = this.progressContainer.querySelector('.free-shipping-progress__element');
    this.progressText = this.progressContainer.querySelector('.free-shipping-progress__text');
    this.currentAmount = this.progressContainer.querySelector('.free-shipping-progress__current');
    this.targetAmount = this.progressContainer.querySelector('.free-shipping-progress__target');
    this.threshold = parseInt(this.progressContainer.dataset.freeShippingThreshold) || 0;
    
    // If we have a last known cart total and the progress bar is visible, update it immediately
    if (this.lastKnownCartTotal > 0 && this.progressContainer.style.display !== 'none') {
      this.updateProgress({ total_price: this.lastKnownCartTotal });
    }
  }

  updateProgressFromCart() {
    // Get cart data from the page
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer) return;
    
    let cartTotal = null;
    
    // Try to get cart data from data attribute first (more reliable)
    const cartDataElement = cartDrawer.querySelector('[data-cart-total]');
    if (cartDataElement) {
      const cartTotalFromData = parseInt(cartDataElement.dataset.cartTotal);
      if (!isNaN(cartTotalFromData)) {
        cartTotal = cartTotalFromData;
      }
    }
    
    // Fall back to reading from totals element
    if (cartTotal === null) {
      const totalsElement = cartDrawer.querySelector('.totals__total-value');
      if (totalsElement) {
        const totalText = totalsElement.textContent.trim();
        cartTotal = this.extractAmountFromCurrency(totalText);
      }
    }
    
    // Only update if we got a valid cart total and it's not 0 (unless cart is actually empty)
    if (cartTotal !== null && cartTotal !== 0) {
      this.lastKnownCartTotal = cartTotal;
      this.updateProgress({ total_price: cartTotal });
    } else if (cartTotal === 0) {
      // If cart total is 0, check if cart is actually empty
      const cartItems = cartDrawer.querySelectorAll('.cart-item');
      if (cartItems.length === 0) {
        this.lastKnownCartTotal = 0;
        this.updateProgress({ total_price: 0 });
      } else {
        // Cart has items but total shows 0, use last known total
        if (this.lastKnownCartTotal > 0) {
          this.updateProgress({ total_price: this.lastKnownCartTotal });
        }
      }
    } else if (cartTotal === null) {
      // If we couldn't read the cart total, use the last known total if available
      if (this.lastKnownCartTotal > 0) {
        this.updateProgress({ total_price: this.lastKnownCartTotal });
      }
    }
  }

  updateProgress(cartData) {
    if (!this.progressContainer || !this.threshold) {
      this.findProgressElements();
      if (!this.progressContainer || !this.threshold) return;
    }
    
    const cartTotal = cartData.total_price || 0;
    
    // Don't update if cart total is 0 and we have a last known total (prevents reverting to 0)
    if (cartTotal === 0 && this.lastKnownCartTotal > 0) {
      return;
    }
    
    if (this.threshold <= 0) return;
    
    const progressPercentage = Math.min((cartTotal / this.threshold) * 100, 100);
    
    // Update progress element
    if (this.progressElement) {
      this.progressElement.value = cartTotal;
      this.progressElement.max = this.threshold;
    }
    
    // Update text
    if (this.progressText) {
      if (cartTotal >= this.threshold) {
        this.progressText.textContent = this.getTranslation('sections.cart.free_shipping_achieved') || 'You’ve unlocked free shipping';
      } else {
        const remaining = this.threshold - cartTotal;
        const remainingText = this.formatCurrency(remaining);
        this.progressText.textContent = `You’re ${remainingText} away from free shipping`;
      }
    }
    
    // Update current amount
    if (this.currentAmount) {
      this.currentAmount.textContent = this.formatCurrency(cartTotal);
    }
    
    // Update target amount
    if (this.targetAmount) {
      this.targetAmount.textContent = this.formatCurrency(this.threshold);
    }
    
    // Add/remove completed class and handle UI changes
    const recommendationsSection = document.querySelector('#shopify-section-cart-drawer-recommendations');
    const drawerFreeShipping = document.querySelector('.drawer-free-shipping');
    const freeShippingDispatchItem = document.querySelector('.lpv2-oos-dispatch-item--free-shipping');
    
    if (cartTotal >= this.threshold) {
      this.progressContainer.classList.add('free-shipping-progress--completed');
      
      // Toggle title-active class on recommendations section
      if (recommendationsSection) {
        recommendationsSection.classList.add('title-active');
      }
      
      // Keep the drawer-free-shipping element visible when unlocked
      if (drawerFreeShipping) {
        drawerFreeShipping.style.display = '';
      }
      
      // Show the free shipping dispatch item
      if (freeShippingDispatchItem) {
        freeShippingDispatchItem.style.display = 'flex';
      }
    } else {
      this.progressContainer.classList.remove('free-shipping-progress--completed');
      
      // Ensure title-active class on recommendations section so cross-sell title is shown
      if (recommendationsSection) {
        recommendationsSection.classList.add('title-active');
      }
      
      // Show the drawer-free-shipping element
      if (drawerFreeShipping) {
        drawerFreeShipping.style.display = '';
      }
      
      // Hide the free shipping dispatch item
      if (freeShippingDispatchItem) {
        freeShippingDispatchItem.style.display = 'none';
      }
    }
  }

  extractAmountFromCurrency(currencyString) {
    // Remove currency symbols and non-numeric characters except decimal point
    const numericString = currencyString.replace(/[^\d.,]/g, '');
    if (numericString.includes(',')) {
      const parts = numericString.split(',');
      if (parts.length > 1 && parts[parts.length - 1].length <= 2) {
        return parseFloat(numericString.replace(/,/g, ''));
      } else {
        return parseFloat(numericString.replace(',', '.'));
      }
    } else {
      return parseFloat(numericString);
    }
  }

  formatCurrency(amount) {
    // Always use the latest Shopify currency
    let currency = 'GBP';
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      currency = window.Shopify.currency.active;
    }
    let locale = 'en-GB';
    if (window.Shopify && window.Shopify.locale) {
      locale = window.Shopify.locale;
    }
    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency
      }).format(amount / 100);
    } catch (e) {
      return `£${(amount / 100).toFixed(2)}`;
    }
  }

  getTranslation(key) {
    if (typeof window.Shopify !== 'undefined' && window.Shopify.translate) {
      return window.Shopify.translate(key);
    }
    const translations = {
      'sections.cart.free_shipping_achieved': window.translation.freeShipping.unlockedMessage,
      'sections.cart.free_shipping_remaining': window.translation.freeShipping.remainingMessage,
      'sections.cart.free_shipping_progress': window.translation.freeShipping.progressMessage
    };
    return translations[key];
  }
}

// Initialize the free shipping progress when DOM is ready
function startFreeShippingProgress() {
  window.__freeShippingProgressInstance = new FreeShippingProgress();
}

document.addEventListener('DOMContentLoaded', startFreeShippingProgress);

// Also re-initialize after every cart drawer render (in case of full DOM replacement)
document.addEventListener('cart-drawer:rendered', () => {
  if (!window.__freeShippingProgressInstance) {
    startFreeShippingProgress();
  }
}); 